import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  sowingsApi,
  type AmendSowingRequest,
  type ReparentSowingRequest,
  type WithdrawSowingRequest,
} from "../services/sowingsApi";

// Invalidate everything that surfaces Sowing rows or aggregates derived from them.
const invalidateSowings = (sowingId?: string, instructionIds?: string[]) => {
  queryClient.invalidateQueries({ queryKey: ["sowings"] });
  queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
  if (sowingId) {
    queryClient.invalidateQueries({ queryKey: ["sowings", sowingId] });
    queryClient.invalidateQueries({ queryKey: ["sowingAmendments", sowingId] });
  }
  instructionIds?.forEach((id) => {
    queryClient.invalidateQueries({ queryKey: ["sowingInstructions", id] });
    queryClient.invalidateQueries({ queryKey: ["sowings", "instruction", id] });
    queryClient.invalidateQueries({
      queryKey: ["sowingAmendments", "instruction", id],
    });
  });
};

export const useAmendSowing = () =>
  useMutation({
    mutationFn: ({
      sowingId,
      payload,
    }: {
      sowingId: string;
      payload: AmendSowingRequest;
    }) => sowingsApi.amend(sowingId, payload),
    onSuccess: (data) => invalidateSowings(data.id, [data.sowingInstructionId]),
  });

export const useWithdrawSowing = () =>
  useMutation({
    mutationFn: ({
      sowingId,
      payload,
      sowingInstructionId,
    }: {
      sowingId: string;
      payload: WithdrawSowingRequest;
      sowingInstructionId: string;
    }) => sowingsApi.withdraw(sowingId, payload),
    onSuccess: (_data, vars) =>
      invalidateSowings(vars.sowingId, [vars.sowingInstructionId]),
  });

export const useReparentSowing = () =>
  useMutation({
    mutationFn: ({
      sowingId,
      payload,
    }: {
      sowingId: string;
      payload: ReparentSowingRequest;
      originalSowingInstructionId: string;
    }) => sowingsApi.reparent(sowingId, payload),
    onSuccess: (data, vars) =>
      invalidateSowings(data.id, [
        vars.originalSowingInstructionId,
        data.sowingInstructionId,
      ]),
  });

export const useSowingAmendments = (sowingId: string | undefined) =>
  useQuery({
    queryKey: ["sowingAmendments", sowingId],
    queryFn: () => sowingsApi.getAmendmentsForSowing(sowingId!),
    enabled: !!sowingId,
  });

export const useSowingsForInstruction = (instructionId: string | undefined) =>
  useQuery({
    queryKey: ["sowings", "instruction", instructionId],
    queryFn: () => sowingsApi.getByInstruction(instructionId!),
    enabled: !!instructionId,
  });

export const useAmendmentsForInstruction = (instructionId: string | undefined) =>
  useQuery({
    queryKey: ["sowingAmendments", "instruction", instructionId],
    queryFn: () => sowingsApi.getAmendmentsForInstruction(instructionId!),
    enabled: !!instructionId,
  });
